import React, { useRef } from "react";
import { Button, ButtonGroup } from "react-bootstrap";
import { EditorContent, useEditor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Image from "@tiptap/extension-image";
import Link from "@tiptap/extension-link";
import Placeholder from "@tiptap/extension-placeholder";
import TextAlign from "@tiptap/extension-text-align";

interface BlogEditorProps {
	initialContentHtml: string;
	initialContentJson: unknown;
	resetToken: string;
	onChange: (payload: { contentHtml: string; contentJson: unknown }) => void;
	onUploadImage: (file: File) => Promise<string>;
	imageMaxHeight: number;
}

const ResizableImage = Image.extend({
	addAttributes() {
		return {
			...this.parent?.(),
			style: {
				default: null,
			},
		};
	},
});

const clampDimension = (value: number, fallback: number) => {
	const parsed = Number(value);
	if (!Number.isFinite(parsed)) {
		return fallback;
	}
	return Math.min(2400, Math.max(120, Math.round(parsed)));
};

const BlogEditor = ({
	initialContentHtml,
	initialContentJson,
	resetToken,
	onChange,
	onUploadImage,
	imageMaxHeight,
}: BlogEditorProps) => {
	const fileInputRef = useRef<HTMLInputElement | null>(null);

	const uploadAndInsertImages = async (files: File[]) => {
		if (!editor) {
			return;
		}
		const maxHeight = clampDimension(imageMaxHeight, 700);
		const imageStyle = `max-height:${maxHeight}px;width:auto;height:auto;`;
		for (const file of files) {
			try {
				const src = await onUploadImage(file);
				const safeSrc = src.replace(/"/g, "&quot;");
				editor
					.chain()
					.focus()
					.insertContent(`<img src="${safeSrc}" style="${imageStyle}" />`)
					.run();
			} catch (error) {
				console.error("Failed to upload image:", error);
				alert("Failed to upload image.");
			}
		}
	};

	const editor = useEditor(
		{
			extensions: [
				StarterKit,
				ResizableImage,
				Link.configure({
					openOnClick: true,
					autolink: true,
					defaultProtocol: "https",
					HTMLAttributes: { rel: "noopener noreferrer", target: "_blank" },
				}),
				Placeholder.configure({
					placeholder: "Write your blog post...",
				}),
				TextAlign.configure({
					types: ["heading", "paragraph"],
				}),
			],
			content: (initialContentJson || initialContentHtml || "<p></p>") as never,
			editorProps: {
				handleDrop: (view, event, slice, moved) => {
					if (moved || !event.dataTransfer?.files?.length) {
						return false;
					}
					const files = Array.from(event.dataTransfer.files).filter((file) =>
						file.type.startsWith("image/"),
					);
					if (!files.length) {
						return false;
					}
					event.preventDefault();
					void uploadAndInsertImages(files);
					return true;
				},
				handlePaste: (view, event) => {
					const files = Array.from(event.clipboardData?.files ?? []).filter(
						(file) => file.type.startsWith("image/"),
					);
					if (!files.length) {
						return false;
					}
					event.preventDefault();
					void uploadAndInsertImages(files);
					return true;
				},
			},
			onUpdate({ editor: activeEditor }) {
				onChange({
					contentHtml: activeEditor.getHTML(),
					contentJson: activeEditor.getJSON(),
				});
			},
		},
		[resetToken],
	);

	const setLink = () => {
		if (!editor) {
			return;
		}
		const current = editor.getAttributes("link").href as string;
		const url = window.prompt("Enter URL", current ?? "");

		if (url === null) {
			return;
		}
		if (url.trim() === "") {
			editor.chain().focus().unsetLink().run();
			return;
		}
		editor.chain().focus().setLink({ href: url.trim() }).run();
	};

	const isHeading = (level: 1 | 2 | 3) =>
		Boolean(editor?.isActive("heading", { level }));

	const insertImageFromPicker = async (
		event: React.ChangeEvent<HTMLInputElement>,
	) => {
		const files = Array.from(event.target.files ?? []).filter((file) =>
			file.type.startsWith("image/"),
		);
		if (!files.length) {
			return;
		}
		await uploadAndInsertImages(files);
		event.target.value = "";
	};

	return (
		<div className="admin-blog-editor">
			<input
				ref={fileInputRef}
				type="file"
				accept="image/*"
				className="d-none"
				onChange={insertImageFromPicker}
			/>
			<ButtonGroup className="admin-blog-editor-toolbar flex-wrap mb-2">
				<Button
					size="sm"
					variant={isHeading(1) ? "primary" : "outline-secondary"}
					onClick={() => editor?.chain().focus().toggleHeading({ level: 1 }).run()}
				>
					H1
				</Button>
				<Button
					size="sm"
					variant={isHeading(2) ? "primary" : "outline-secondary"}
					onClick={() => editor?.chain().focus().toggleHeading({ level: 2 }).run()}
				>
					H2
				</Button>
				<Button
					size="sm"
					variant={isHeading(3) ? "primary" : "outline-secondary"}
					onClick={() => editor?.chain().focus().toggleHeading({ level: 3 }).run()}
				>
					H3
				</Button>
				<Button
					size="sm"
					variant={editor?.isActive("bold") ? "primary" : "outline-secondary"}
					onClick={() => editor?.chain().focus().toggleBold().run()}
				>
					Bold
				</Button>
				<Button
					size="sm"
					variant={editor?.isActive("italic") ? "primary" : "outline-secondary"}
					onClick={() => editor?.chain().focus().toggleItalic().run()}
				>
					Italic
				</Button>
				<Button
					size="sm"
					variant={
						editor?.isActive("bulletList") ? "primary" : "outline-secondary"
					}
					onClick={() => editor?.chain().focus().toggleBulletList().run()}
				>
					Bullets
				</Button>
				<Button
					size="sm"
					variant={
						editor?.isActive("orderedList") ? "primary" : "outline-secondary"
					}
					onClick={() => editor?.chain().focus().toggleOrderedList().run()}
				>
					Numbers
				</Button>
				<Button
					size="sm"
					variant={
						editor?.isActive("blockquote") ? "primary" : "outline-secondary"
					}
					onClick={() => editor?.chain().focus().toggleBlockquote().run()}
				>
					Quote
				</Button>
				<Button
					size="sm"
					variant={editor?.isActive("link") ? "primary" : "outline-secondary"}
					onClick={setLink}
				>
					Link
				</Button>
				<Button
					size="sm"
					variant="outline-secondary"
					onClick={() => fileInputRef.current?.click()}
				>
					Image
				</Button>
			</ButtonGroup>
			<div className="admin-blog-editor-content">
				<EditorContent editor={editor} />
			</div>
		</div>
	);
};

export default BlogEditor;
